import React, { useRef, useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { X, Pen, Type, Upload, Check, RotateCcw } from 'lucide-react';

// Signature font styles for "Type" option
const SIGNATURE_FONTS = [
  { name: 'Elegant', fontFamily: "'Dancing Script', cursive", className: 'font-dancing' },
  { name: 'Classic', fontFamily: "'Great Vibes', cursive", className: 'font-vibes' },
  { name: 'Modern', fontFamily: "'Pacifico', cursive", className: 'font-pacifico' },
  { name: 'Professional', fontFamily: "'Allura', cursive", className: 'font-allura' },
  { name: 'Bold', fontFamily: "'Satisfy', cursive", className: 'font-satisfy' },
];

const SignatureModal = ({ onSave, onCancel, signerName = '' }) => {
  const [activeTab, setActiveTab] = useState('draw'); // 'draw', 'type', 'upload'
  const [typedName, setTypedName] = useState(signerName);
  const [selectedFont, setSelectedFont] = useState(SIGNATURE_FONTS[0]);
  const [uploadedImage, setUploadedImage] = useState(null);
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  // Load Google Fonts for signature styles
  useEffect(() => {
    const link = document.createElement('link');
    link.href = 'https://fonts.googleapis.com/css2?family=Dancing+Script:wght@700&family=Great+Vibes&family=Pacifico&family=Allura&family=Satisfy&display=swap';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
    return () => document.head.removeChild(link);
  }, []);

  // Canvas drawing functions
  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const { x, y } = getCoordinates(e);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.lineTo(x, y);
    ctx.strokeStyle = '#1e293b';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.stroke();
    setHasDrawn(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  // Generate signature image from typed text
  const generateTypedSignature = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 600;
    canvas.height = 200;
    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    // Draw text
    ctx.fillStyle = '#1e293b';
    ctx.font = `64px ${selectedFont.fontFamily}`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(typedName, canvas.width / 2, canvas.height / 2);
    
    return canvas.toDataURL('image/png');
  };

  // Handle file upload
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        setUploadedImage(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Save signature based on active tab
  const handleSave = () => {
    let signatureData;
    let signatureType;
    
    switch (activeTab) {
      case 'draw':
        signatureData = canvasRef.current.toDataURL('image/png');
        signatureType = 'drawn';
        break;
      case 'type':
        signatureData = generateTypedSignature();
        signatureType = 'typed';
        break;
      case 'upload':
        signatureData = uploadedImage;
        signatureType = 'uploaded';
        break;
      default:
        return;
    }
    
    onSave({
      signatureData,
      signatureType,
      signerName: typedName || signerName,
      timestamp: new Date().toISOString(),
    });
  };

  // Check if save should be enabled
  const canSave = () => {
    switch (activeTab) {
      case 'draw':
        return hasDrawn;
      case 'type':
        return typedName.trim().length > 0;
      case 'upload':
        return uploadedImage !== null;
      default:
        return false;
    }
  };

  const tabs = [
    { id: 'draw', label: 'Draw', icon: Pen },
    { id: 'type', label: 'Type', icon: Type },
    { id: 'upload', label: 'Upload', icon: Upload },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-2xl glass-effect border-slate-200 dark:border-slate-700 animate-scale-in">
        <CardHeader className="border-b border-slate-200 dark:border-slate-700">
          <CardTitle className="flex justify-between items-center">
            <span className="text-slate-900 dark:text-slate-100">Add Your Signature</span>
            <Button variant="ghost" size="sm" onClick={onCancel}>
              <X className="w-5 h-5" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6 space-y-6">
          {/* Tab Navigation */}
          <div className="flex gap-2 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-md text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-200'
                }`}
                data-testid={`signature-tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>

          {/* Draw Tab */}
          {activeTab === 'draw' && (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-2 bg-white dark:bg-slate-800">
                <canvas
                  ref={canvasRef}
                  width={600}
                  height={200}
                  className="w-full rounded cursor-crosshair touch-none"
                  style={{ aspectRatio: '3/1' }}
                  onMouseDown={startDrawing}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                  onTouchStart={startDrawing}
                  onTouchMove={draw}
                  onTouchEnd={stopDrawing}
                  data-testid="signature-canvas"
                />
              </div>
              <div className="flex items-center justify-between">
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  Draw your signature above using mouse or touch
                </p>
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={clearCanvas}
                  className="gap-2"
                >
                  <RotateCcw className="w-4 h-4" />
                  Clear
                </Button>
              </div>
            </div>
          )}

          {/* Type Tab */}
          {activeTab === 'type' && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="typed-name">Type your full name</Label>
                <Input
                  id="typed-name"
                  value={typedName}
                  onChange={(e) => setTypedName(e.target.value)}
                  placeholder="Enter your name"
                  className="mt-2"
                  data-testid="signature-type-input"
                />
              </div>
              
              <div>
                <Label>Select a signature style</Label>
                <div className="grid grid-cols-1 gap-3 mt-2">
                  {SIGNATURE_FONTS.map((font) => (
                    <button
                      key={font.name}
                      onClick={() => setSelectedFont(font)}
                      className={`p-4 rounded-lg border-2 text-left transition-all ${
                        selectedFont.name === font.name
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600'
                      }`}
                      data-testid={`signature-font-${font.name.toLowerCase()}`}
                    >
                      <div className="flex items-center justify-between">
                        <span
                          className="text-3xl text-slate-900 dark:text-slate-100"
                          style={{ fontFamily: font.fontFamily }}
                        >
                          {typedName || 'Your Name'}
                        </span>
                        {selectedFont.name === font.name && (
                          <Check className="w-5 h-5 text-blue-500" />
                        )}
                      </div>
                      <span className="text-xs text-slate-500 dark:text-slate-400 mt-1 block">
                        {font.name}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Upload Tab */}
          {activeTab === 'upload' && (
            <div className="space-y-4">
              <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-6 text-center bg-slate-50 dark:bg-slate-800/50">
                {uploadedImage ? (
                  <div className="space-y-4">
                    <img
                      src={uploadedImage}
                      alt="Uploaded signature"
                      className="max-h-32 mx-auto"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setUploadedImage(null)}
                      className="gap-2"
                    >
                      <RotateCcw className="w-4 h-4" />
                      Remove & Upload New
                    </Button>
                  </div>
                ) : (
                  <label className="cursor-pointer block">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                      data-testid="signature-upload-input"
                    />
                    <Upload className="w-12 h-12 mx-auto text-slate-400 mb-3" />
                    <p className="text-slate-600 dark:text-slate-400 font-medium">
                      Click to upload signature image
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-500 mt-1">
                      PNG, JPG up to 5MB
                    </p>
                  </label>
                )}
              </div>
            </div>
          )}

          {/* Legal Notice */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
            <p className="text-xs text-amber-700 dark:text-amber-400">
              By signing, you agree that this electronic signature is legally binding and equivalent to your handwritten signature. 
              Your signature will be recorded with a timestamp and associated with your account.
            </p>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-2">
            <Button 
              variant="outline" 
              onClick={onCancel} 
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={!canSave()}
              className="flex-1 gradient-primary border-0"
              data-testid="save-signature-btn"
            >
              <Check className="w-4 h-4 mr-2" />
              Apply Signature
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SignatureModal;
